import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "hm_theme";
const THEMES = ["light", "dark", "system"];

const ThemeContext = createContext({
  theme: "system",
  resolved: "light",
  setTheme: () => {},
});

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(v) ? v : "system";
  } catch {
    return "system";
  }
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const applyClass = (resolved) => {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);
  const [resolved, setResolved] = useState(
    theme === "system" ? (systemPrefersDark() ? "dark" : "light") : theme
  );

  // Apply the resolved theme to <html> whenever it changes
  useEffect(() => { applyClass(resolved); }, [resolved]);

  // Recompute resolved whenever the user changes preference
  useEffect(() => {
    if (theme === "system") {
      setResolved(systemPrefersDark() ? "dark" : "light");
    } else {
      setResolved(theme);
    }
  }, [theme]);

  // Listen to OS-level theme flips when user is on "system"
  useEffect(() => {
    if (theme !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setResolved(e.matches ? "dark" : "light");
    // Older Safari uses addListener/removeListener
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setThemeState(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
